import { Loader } from "@/components/Common/Loader/Loader";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import { InfoIcon } from "@/components/Icons/InfoIcon";
import { Separator } from "@/components/ui/separator";
import { formatPrice } from "../SummaryPrice";
import { cn } from "@/lib/utils";
import { useFormikContext } from "formik";
import { SimpleClusterForm } from "@/types";
import { BalanceType } from "@/types/user";
import { useUserStore } from "@/store/user";
import ENV_VARIABLES from "@/env";
import { StandardContainer } from "@/components/Common/StandardContainer/StandardContainer";
import {
  PAYG_HOURS_PER_MONTH,
  getPaygMonthlyEstimate,
  getPaygSuggestedTopUp,
  isPaygCluster
} from "@/utils/payg";
import { PurchasedCreditsRequirement } from "../VerticalFlowComponents/components/PurchasedCreditsRequirement";
import type { PurchasedCreditsGate } from "../VerticalFlowComponents/hooks/usePurchasedCreditsGate";

type InlinePaymentSectionProps = {
  isPriceLoaded: boolean;
  totalCost: number | string | undefined;
  showSavingsMessage: boolean;
  totalSavingsAmountFormatted: string;
  paymentLink?: string;
  /** Only shown in development for crypto mock payments */
  onMockPayment?: () => void;
  purchasedCreditsGate?: PurchasedCreditsGate;
  minNonPromotionalCreditsRequired?: number;
};

/**
 * Deploys are always paid from IO Credits, so this section states the method rather
 * than offering a choice: the confirm modal handles topping up a short balance.
 */
export const InlinePaymentSection: React.FC<InlinePaymentSectionProps> = ({
  isPriceLoaded,
  totalCost,
  showSavingsMessage,
  totalSavingsAmountFormatted,
  paymentLink,
  onMockPayment,
  purchasedCreditsGate,
  minNonPromotionalCreditsRequired = 0
}) => {
  const { values } = useFormikContext<SimpleClusterForm>();
  const balances = useUserStore((state) => state.balances);

  const isPayg = isPaygCluster(values.clusterType);
  const hourlyCost = isNaN(Number(totalCost)) ? 0 : Number(totalCost);
  const creditBalance = Number(balances?.[BalanceType.CREDITS] ?? 0);
  const isCreditsShort = isPriceLoaded && hourlyCost > 0 && creditBalance < hourlyCost;
  const suggestedTopUp = getPaygSuggestedTopUp(hourlyCost, creditBalance);

  return (
    <div className="flex flex-col gap-4">
      {/* ── separator above Total Cost ── */}
      <Separator className="my-1" />

      <StandardContainer>
        {/* Total Cost row (hourly rate under PAYG) */}
        <div className={cn("flex h-[28px] items-center justify-between")}>
          <span className="inline-flex items-center gap-1.5 text-[16px] font-medium leading-[24px] text-foreground">
            <span>{isPayg ? "Cost per hour" : "Total Cost"}</span>
            {isPayg && (
              <SimpleTooltip
                id="payg-hourly-price-tooltip"
                content="This is the current hourly rate. Your rate may change while the cluster is running based on supplier pricing. Future charges use the latest rate."
                place="top"
                usePortal
                tooltipClassName="!max-w-[300px] whitespace-normal"
              >
                <InfoIcon className="h-4 w-4 shrink-0 cursor-help text-muted-foreground" />
              </SimpleTooltip>
            )}
          </span>
          {isPriceLoaded ? (
            <span className="text-[20px] font-medium leading-[28px] text-foreground">
              {formatPrice("credits", hourlyCost, 2)}
              {isPayg && <span className="text-[14px] text-muted-foreground">/hr</span>}
            </span>
          ) : (
            <Loader className="h-[20px]" />
          )}
        </div>

        {isPayg && isPriceLoaded && (
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[14px] leading-[20px] text-muted-foreground">
              Est. monthly cost ({PAYG_HOURS_PER_MONTH} hrs)
            </span>
            <span className="text-[14px] font-medium leading-[20px] text-foreground">
              {formatPrice("credits", getPaygMonthlyEstimate(hourlyCost), 2)}
            </span>
          </div>
        )}

        {isPriceLoaded && (
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[14px] leading-[20px] text-muted-foreground">
              Paying with IO Credits
            </span>
            <span className="text-[14px] leading-[20px] text-muted-foreground">
              Balance {formatPrice("credits", creditBalance, 2)}
            </span>
          </div>
        )}

        {isCreditsShort && isPayg && (
          <div className="mt-2 rounded-[8px] bg-muted px-4 py-2 text-[12px] leading-[18px] text-foreground">
            Your credits balance ({formatPrice("credits", creditBalance, 2)}) doesn’t cover the
            first hour of usage. Deploying will open a purchase of{" "}
            <strong>{formatPrice("credits", suggestedTopUp, 2)}</strong> in credits
            {suggestedTopUp > hourlyCost - creditBalance ? " ($10 minimum purchase)" : ""}.
          </div>
        )}

        {isCreditsShort && !isPayg && (
          <div className="mt-2 rounded-[8px] bg-muted px-4 py-2 text-[12px] leading-[18px] text-foreground">
            Your credits balance ({formatPrice("credits", creditBalance, 2)}) doesn’t cover this
            deployment. Deploying will open a purchase for the difference, payable by card or USDC.
          </div>
        )}

        {showSavingsMessage && (
          <div className="mt-2 rounded-[8px] bg-[#00ac4d3d] px-4 py-2 text-[12px] leading-[18px]">
            Congratulations! You have saved{" "}
            <strong className="text-[13px]">{totalSavingsAmountFormatted}</strong> compared to
            typical pricing from other providers for the same hardware. 🎉
          </div>
        )}

        {purchasedCreditsGate && (
          <PurchasedCreditsRequirement
            gate={purchasedCreditsGate}
            required={minNonPromotionalCreditsRequired}
            className="mt-2"
          />
        )}
      </StandardContainer>

      <div className="flex-1"></div>

      <StandardContainer>
        {/* Dev-only mock payment button for the USDC credits top-up link */}
        {ENV_VARIABLES.ENVIRONMENT === "development" &&
          ENV_VARIABLES.SPHERE_WEBHOOK_URL &&
          paymentLink &&
          onMockPayment && (
            <button
              type="button"
              onClick={onMockPayment}
              className="w-full rounded-[8px] border border-border bg-muted py-2 text-[14px] text-foreground hover:bg-muted/80"
            >
              Make Mock Payment
            </button>
          )}
      </StandardContainer>
    </div>
  );
};
